import { CreateGroupModal } from '../../component/groups/create-group-modal'
import { PageContainer } from 'src/component/page-container/page-container'
import { Button } from '../../component/ui/button'
import { Input } from '../../component/ui/input/input'
import { PageTitle } from '../../component/ui/page-title'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminGroups, useDeleteAdminGroup } from 'src/hook/use-groups'
import { isFeatureFlagged } from 'src/util/feature-flags'
import { toasterUtil } from 'src/util/toaster-util'

export const AdminGroupsScreen = (): React.ReactElement => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const limit = 20

  const { data, isLoading, error, refetch } = useAdminGroups({ limit, offset, search })
  const deleteMutation = useDeleteAdminGroup()

  useEffect(() => {
    if (isFeatureFlagged('GROUPS')) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    if (error && (error as any)?.response?.status === 403) {
      navigate('/', { replace: true })
    }
  }, [error, navigate])

  const handleDelete = (groupId: string, groupName: string): void => {
    if (window.confirm(`Are you sure you want to delete "${groupName}"?`)) {
      deleteMutation.mutate(groupId, {
        onSuccess: () => {
          toasterUtil.success('Group deleted successfully')
          refetch()
        },
        onError: () => {
          toasterUtil.error('Failed to delete group')
        },
      })
    }
  }

  const groups = data?.data ?? []
  const total = data?.total ?? 0
  const hasMore = offset + limit < total
  const hasPrevious = offset > 0

  return (
    <PageContainer>
      <div className="py-6 md:py-[82px]">
        <div className="mb-6 flex items-center justify-between">
          <PageTitle>Admin - Groups</PageTitle>
          <Button onClick={() => setIsCreateModalOpen(true)}>Create Group</Button>
        </div>

        <div className="mb-6">
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setOffset(0)
            }}
            placeholder="Search groups..."
          />
        </div>

        {isLoading ? (
          <div className="bg-dark-gray rounded-lg p-6">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        ) : groups.length === 0 ? (
          <div className="bg-dark-gray rounded-lg p-6 text-center">
            <p className="text-gray-400">No groups found</p>
          </div>
        ) : (
          <div className="bg-dark-gray rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Members</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {groups.map((group) => (
                  <tr key={group.id} className="hover:bg-gray-800">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-ivory font-semibold">{group.name}</div>
                        {group.description && <div className="text-gray-400 text-sm truncate max-w-xs">{group.description}</div>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300 capitalize">{group.type}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{group.memberCount ?? 0}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          group.isActive ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                        }`}
                      >
                        {group.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">{new Date(group.createdAt).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => navigate(`/admin/groups/${group.id}`)}
                        className="text-blue-400 hover:text-blue-300 text-sm font-semibold"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(group.id, group.name)}
                        className="text-red-400 hover:text-red-300 text-sm font-semibold"
                        disabled={deleteMutation.isLoading}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(hasMore || hasPrevious) && (
          <div className="flex justify-between items-center mt-6">
            <Button variant="secondary" onClick={() => setOffset(offset - limit)} disabled={!hasPrevious}>
              Previous
            </Button>
            <span className="text-gray-400">
              Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
            </span>
            <Button variant="secondary" onClick={() => setOffset(offset + limit)} disabled={!hasMore}>
              Next
            </Button>
          </div>
        )}
      </div>

      <CreateGroupModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries(['admin-groups'])
          refetch()
        }}
      />
    </PageContainer>
  )
}
